import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical, Plus, Trash2, Eye, Monitor, Tablet, Smartphone, Copy, Save, X,
  LayoutTemplate, Newspaper, Star, Clock, Grid3x3, FolderKanban, TrendingUp,
  Play, Radio, Sparkles, Megaphone, Loader2,
} from "lucide-react";
import ArticlePicker from "@/components/admin/ArticlePicker";
import { formatDateShort } from "@/lib/date";

const BLOCK_KINDS = [
  { value: "main", label: "Noticia principal", icon: Newspaper },
  { value: "featured", label: "Destacadas", icon: Star },
  { value: "today", label: "Hoy en Sur Primicia (carrusel)", icon: Clock },
  { value: "latest", label: "Últimas noticias", icon: Clock },
  { value: "grid", label: "Grilla de noticias", icon: Grid3x3 },
  { value: "category", label: "Bloque por categoría", icon: FolderKanban },
  { value: "most_read", label: "Más leídas", icon: TrendingUp },
  { value: "videos", label: "Videos destacados", icon: Play },
  { value: "recommended", label: "Recomendadas", icon: Sparkles },
  { value: "breaking", label: "Último momento", icon: Megaphone },
  { value: "live", label: "Señal en vivo", icon: Radio },
  { value: "ad_slot", label: "Espacio publicitario", icon: LayoutTemplate },
];

const KIND_META = Object.fromEntries(BLOCK_KINDS.map((k) => [k.value, k]));

// 6 composiciones editoriales para el bloque "main". Coinciden con el
// renderer en HomePage.jsx (variantes A-F).
const LAYOUT_VARIANTS = [
  { key: "A", label: "Principal izq. + 3 der.", hint: "Composición clásica" },
  { key: "B", label: "Principal der. + 3 izq.", hint: "Espejo, mirada nueva" },
  { key: "C", label: "Principal arriba + 3 abajo", hint: "Titular protagonista" },
  { key: "D", label: "Grid editorial", hint: "Hero grande + 3 balanceadas" },
  { key: "E", label: "Principal + 2 + 1 ancho", hint: "Jerarquía en cascada" },
  { key: "F", label: "Principal + 2 col. + 1 ancho", hint: "Foco y contexto" },
];

// Minimal SVG thumbnail representing each layout variant.
function LayoutThumb({ variant }) {
  const V = String(variant || "A").toUpperCase();
  const s = "stroke-nightblue fill-softblue/40";
  const p = "fill-turquoise/70 stroke-nightblue";
  return (
    <svg viewBox="0 0 100 60" className="w-full h-16 border border-softblue bg-white">
      {V === "A" && (
        <>
          <rect x="4" y="4" width="60" height="52" className={p} strokeWidth="1" />
          <rect x="68" y="4" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="68" y="22" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="68" y="40" width="28" height="16" className={s} strokeWidth="1" />
        </>
      )}
      {V === "B" && (
        <>
          <rect x="4" y="4" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="4" y="22" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="4" y="40" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="36" y="4" width="60" height="52" className={p} strokeWidth="1" />
        </>
      )}
      {V === "C" && (
        <>
          <rect x="4" y="4" width="92" height="30" className={p} strokeWidth="1" />
          <rect x="4" y="38" width="28" height="18" className={s} strokeWidth="1" />
          <rect x="36" y="38" width="28" height="18" className={s} strokeWidth="1" />
          <rect x="68" y="38" width="28" height="18" className={s} strokeWidth="1" />
        </>
      )}
      {V === "D" && (
        <>
          <rect x="4" y="4" width="52" height="52" className={p} strokeWidth="1" />
          <rect x="60" y="4" width="36" height="16" className={s} strokeWidth="1" />
          <rect x="60" y="22" width="36" height="16" className={s} strokeWidth="1" />
          <rect x="60" y="40" width="36" height="16" className={s} strokeWidth="1" />
        </>
      )}
      {V === "E" && (
        <>
          <rect x="4" y="4" width="60" height="34" className={p} strokeWidth="1" />
          <rect x="68" y="4" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="68" y="22" width="28" height="16" className={s} strokeWidth="1" />
          <rect x="4" y="42" width="92" height="14" className={s} strokeWidth="1" />
        </>
      )}
      {V === "F" && (
        <>
          <rect x="4" y="4" width="92" height="26" className={p} strokeWidth="1" />
          <rect x="4" y="34" width="44" height="14" className={s} strokeWidth="1" />
          <rect x="52" y="34" width="44" height="14" className={s} strokeWidth="1" />
          <rect x="4" y="50" width="92" height="6" className={s} strokeWidth="1" />
        </>
      )}
    </svg>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/Argentina/Ushuaia",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const p = fmt.formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch { return ""; }
}
function fromLocalInput(v) {
  if (!v) return null;
  return new Date(`${v}:00-03:00`).toISOString();
}

// Sortable item wrapper
function SortableBlockRow({ block, active, categories, onSelect, onToggle, onDelete, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const Icon = KIND_META[block.kind]?.icon || LayoutTemplate;
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isActive = active?.id === block.id;
  const catLabel = block.kind === "category" && block.category_slug ? ` · ${block.category_slug}` : "";
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={ATID.homepageBlockRow(block.id)}
      className={`flex items-center gap-2 border ${isActive ? "border-nightblue bg-lightgray/40" : "border-softblue bg-white"} px-2 py-2`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-textgray hover:text-nightblue h-8 w-6 flex items-center justify-center" title="Arrastrar">
        <GripVertical className="w-4 h-4" />
      </button>
      <button onClick={() => onSelect(block)} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-turquoise flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-blueblack text-sm truncate">
              {block.title || KIND_META[block.kind]?.label || block.kind}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-textgray">
              {block.kind}{catLabel} · {block.mode} · {block.limit} items
            </div>
          </div>
        </div>
      </button>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onDuplicate(block)}
          data-testid={ATID.homepageBlockDuplicate(block.id)}
          title="Duplicar"
          className="h-7 w-7 border border-softblue text-textgray hover:text-nightblue hover:bg-lightgray inline-flex items-center justify-center"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(block)}
          data-testid={ATID.homepageBlockDelete(block.id)}
          title="Eliminar"
          className="h-7 w-7 border border-softblue text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <Switch
          data-testid={ATID.homepageBlockToggle(block.id)}
          checked={block.active !== false}
          onCheckedChange={() => onToggle(block)}
        />
      </div>
    </div>
  );
}

// Sortable manual article row
function SortableArticleRow({ article, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: article.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={ATID.homepageManualArticle(article.id)}
      className="flex items-center gap-2 border border-softblue bg-white px-2 py-1.5"
    >
      <button {...attributes} {...listeners} className="cursor-grab text-textgray hover:text-nightblue h-6 w-5 flex items-center justify-center" title="Arrastrar">
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="w-8 h-8 bg-lightgray flex-shrink-0 overflow-hidden">
        {article.main_image && <img src={article.main_image} alt="" className="w-full h-full object-cover" loading="lazy" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-blueblack line-clamp-1">{article.title}</div>
        <div className="text-[10px] uppercase tracking-widest text-textgray">
          {article.main_category_name} · {formatDateShort(article.published_at)}
        </div>
      </div>
      <button
        onClick={() => onRemove(article.id)}
        data-testid={ATID.homepageManualArticleRemove(article.id)}
        className="h-6 w-6 border border-softblue text-textgray hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
        title="Quitar"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// -------------------- MAIN PAGE --------------------
export default function AdminHomepageManagerPage() {
  const [blocks, setBlocks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // block being edited
  const [manualArticles, setManualArticles] = useState([]); // resolved article docs
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [blks, cats] = await Promise.all([api.adminListBlocks(), api.categories()]);
      setBlocks(blks);
      setCategories(cats);
      if (!selectedId && blks.length > 0) {
        setSelectedId(blks[0].id);
      }
    } catch {
      toast.error("No se pudieron cargar los bloques.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sync draft whenever selectedId changes
  useEffect(() => {
    if (!selectedId) { setDraft(null); return; }
    const block = blocks.find((b) => b.id === selectedId);
    if (!block) { setDraft(null); return; }
    setDraft({
      id: block.id,
      kind: block.kind,
      title: block.title || "",
      active: block.active !== false,
      device_visibility: {
        desktop: block.device_visibility?.desktop !== false,
        tablet: block.device_visibility?.tablet !== false,
        mobile: block.device_visibility?.mobile !== false,
      },
      mode: block.mode || "auto",
      category_slug: block.category_slug || "",
      limit: block.limit || 6,
      schedule_start: toLocalInput(block.schedule_start),
      schedule_end: toLocalInput(block.schedule_end),
      replacement_block_id: block.replacement_block_id || "",
      manual_article_ids: block.manual_article_ids || [],
      layout_variant: (block.layout_variant || "A").toUpperCase(),
      config: block.config || {},
    });
    // Load referenced manual articles for preview
    if ((block.manual_article_ids || []).length > 0) {
      api.adminListArticles({ limit: 100 }).then((r) => {
        const byId = Object.fromEntries(r.items.map((a) => [a.id, a]));
        setManualArticles(block.manual_article_ids.map((id) => byId[id]).filter(Boolean));
      }).catch(() => setManualArticles([]));
    } else {
      setManualArticles([]);
    }
  }, [selectedId, blocks]);

  const onDragEndBlocks = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(blocks, oldIdx, newIdx);
    setBlocks(next);
    try {
      await api.adminReorderBlocks(next.map((b) => b.id));
    } catch {
      toast.error("No se pudo guardar el orden.");
      load();
    }
  };

  const onDragEndArticles = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = manualArticles.findIndex((a) => a.id === active.id);
    const newIdx = manualArticles.findIndex((a) => a.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const nextArts = arrayMove(manualArticles, oldIdx, newIdx);
    setManualArticles(nextArts);
    const nextIds = nextArts.map((a) => a.id);
    setDraft((d) => ({ ...d, manual_article_ids: nextIds }));
    try {
      await api.adminSetBlockArticles(draft.id, nextIds);
      setBlocks((prev) => prev.map((b) => b.id === draft.id ? { ...b, manual_article_ids: nextIds } : b));
    } catch {
      toast.error("No se pudo guardar el orden.");
    }
  };

  const buildPayload = (d) => ({
    kind: d.kind,
    title: d.title,
    order: 0, // preserved via reorder endpoint
    active: d.active,
    device_visibility: d.device_visibility,
    mode: d.mode,
    category_slug: d.kind === "category" ? d.category_slug || null : d.category_slug || null,
    limit: parseInt(d.limit || 0, 10) || 1,
    manual_article_ids: d.manual_article_ids || [],
    layout_variant: d.kind === "main" ? (d.layout_variant || "A") : null,
    schedule_start: fromLocalInput(d.schedule_start),
    schedule_end: fromLocalInput(d.schedule_end),
    replacement_block_id: d.replacement_block_id || null,
    config: d.config || {},
  });

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = buildPayload(draft);
      const saved = await api.adminUpdateBlock(draft.id, payload);
      setBlocks((prev) => prev.map((b) => b.id === saved.id ? saved : b));
      toast.success("Bloque guardado.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    setSaving(true);
    try {
      const payload = {
        kind: "latest",
        title: "Nuevo bloque",
        order: (blocks.length + 1) * 10,
        active: false,
        device_visibility: { desktop: true, tablet: true, mobile: true },
        mode: "auto",
        limit: 6,
        manual_article_ids: [],
      };
      const created = await api.adminCreateBlock(payload);
      setBlocks((prev) => [...prev, created]);
      setSelectedId(created.id);
      toast.success("Bloque creado.");
    } catch {
      toast.error("No se pudo crear el bloque.");
    } finally {
      setSaving(false);
    }
  };

  const duplicateBlock = async (b) => {
    try {
      const payload = { ...b, title: `${b.title || KIND_META[b.kind]?.label} (copia)`, active: false };
      delete payload.id;
      delete payload.order;
      delete payload.created_at;
      delete payload.updated_at;
      const created = await api.adminCreateBlock(payload);
      setBlocks((prev) => [...prev, created]);
      setSelectedId(created.id);
      toast.success("Bloque duplicado.");
    } catch {
      toast.error("No se pudo duplicar.");
    }
  };

  const toggleActive = async (b) => {
    try {
      const saved = await api.adminUpdateBlock(b.id, {
        kind: b.kind, title: b.title || "", order: b.order || 0,
        active: !b.active, device_visibility: b.device_visibility || { desktop: true, tablet: true, mobile: true },
        mode: b.mode || "auto", category_slug: b.category_slug || null,
        limit: b.limit || 6, manual_article_ids: b.manual_article_ids || [],
        schedule_start: b.schedule_start || null, schedule_end: b.schedule_end || null,
        replacement_block_id: b.replacement_block_id || null, config: b.config || {},
      });
      setBlocks((prev) => prev.map((x) => x.id === saved.id ? saved : x));
    } catch {
      toast.error("No se pudo cambiar el estado.");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.adminDeleteBlock(confirmDelete.id);
      setBlocks((prev) => prev.filter((b) => b.id !== confirmDelete.id));
      if (selectedId === confirmDelete.id) setSelectedId(null);
      setConfirmDelete(null);
      toast.success("Bloque eliminado.");
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const onPickArticles = async (articles) => {
    const ids = [...(draft.manual_article_ids || []), ...articles.map((a) => a.id)];
    setDraft((d) => ({ ...d, manual_article_ids: ids }));
    setManualArticles((prev) => [...prev, ...articles]);
    try {
      await api.adminSetBlockArticles(draft.id, ids);
      setBlocks((prev) => prev.map((b) => b.id === draft.id ? { ...b, manual_article_ids: ids } : b));
      toast.success("Noticias agregadas.");
    } catch {
      toast.error("No se pudieron guardar.");
    }
  };

  const removeManual = async (articleId) => {
    const ids = (draft.manual_article_ids || []).filter((i) => i !== articleId);
    setDraft((d) => ({ ...d, manual_article_ids: ids }));
    setManualArticles((prev) => prev.filter((a) => a.id !== articleId));
    try {
      await api.adminSetBlockArticles(draft.id, ids);
      setBlocks((prev) => prev.map((b) => b.id === draft.id ? { ...b, manual_article_ids: ids } : b));
    } catch {
      toast.error("No se pudo actualizar.");
    }
  };

  return (
    <div data-testid={ATID.homepagePage}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Diseño</div>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Gestor de portada</h1>
          <p className="mt-1 text-sm text-textgray">Reordená bloques con arrastrar y soltar. Los cambios se reflejan en el portal público.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid={ATID.homepagePreviewButton}
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            className="rounded-none h-10 border-softblue text-nightblue hover:bg-lightgray uppercase text-xs tracking-widest font-semibold"
          >
            <Eye className="w-4 h-4 mr-2" /> Vista previa
          </Button>
          <Button
            data-testid={ATID.homepageAddBlock}
            onClick={addBlock}
            className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 px-4 uppercase text-xs tracking-widest font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo bloque
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Blocks list */}
        <section className="bg-lightgray/40 border border-softblue p-3">
          <div className="text-xs uppercase tracking-widest text-textgray font-semibold mb-2 px-1">
            Bloques ({blocks.length})
          </div>
          {loading ? (
            <div className="p-6 text-sm text-textgray">Cargando…</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndBlocks}>
              <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                <div data-testid={ATID.homepageBlocksList} className="space-y-1.5">
                  {blocks.map((b) => (
                    <SortableBlockRow
                      key={b.id}
                      block={b}
                      active={draft}
                      categories={categories}
                      onSelect={(x) => setSelectedId(x.id)}
                      onToggle={toggleActive}
                      onDelete={setConfirmDelete}
                      onDuplicate={duplicateBlock}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>

        {/* Editor */}
        <section className="bg-white border border-softblue p-5 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto">
          {!draft ? (
            <div className="p-6 text-sm text-textgray text-center">Elegí un bloque para editarlo.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-xl font-bold text-blueblack">Configuración</h2>
                <Button
                  data-testid={ATID.homepageSaveBlock}
                  onClick={saveDraft}
                  disabled={saving}
                  className="bg-nightblue hover:bg-blueblack text-white rounded-none h-9 uppercase text-xs tracking-widest font-semibold"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Título del bloque</Label>
                <Input
                  data-testid={ATID.homepageEditorTitle}
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Tipo</Label>
                  <select
                    data-testid={ATID.homepageEditorKind}
                    value={draft.kind}
                    onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                    className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                  >
                    {BLOCK_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Modo</Label>
                  <select
                    data-testid={ATID.homepageEditorMode}
                    value={draft.mode}
                    onChange={(e) => setDraft({ ...draft, mode: e.target.value })}
                    className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                  >
                    <option value="auto">Automático</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Categoría</Label>
                  <select
                    data-testid={ATID.homepageEditorCategory}
                    value={draft.category_slug}
                    onChange={(e) => setDraft({ ...draft, category_slug: e.target.value })}
                    className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                  >
                    <option value="">Sin categoría (global)</option>
                    {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Cantidad</Label>
                  <Input
                    data-testid={ATID.homepageEditorLimit}
                    type="number"
                    min={1}
                    value={draft.limit}
                    onChange={(e) => setDraft({ ...draft, limit: e.target.value })}
                    className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold mb-2 block">Visible en</Label>
                <div className="flex gap-2">
                  {["desktop", "tablet", "mobile"].map((dev) => {
                    const Icon = dev === "desktop" ? Monitor : dev === "tablet" ? Tablet : Smartphone;
                    return (
                      <button
                        key={dev}
                        type="button"
                        data-testid={dev === "desktop" ? ATID.homepageEditorVisibilityDesktop : dev === "tablet" ? ATID.homepageEditorVisibilityTablet : ATID.homepageEditorVisibilityMobile}
                        onClick={() => setDraft({ ...draft, device_visibility: { ...draft.device_visibility, [dev]: !draft.device_visibility[dev] } })}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 border text-xs uppercase tracking-widest font-semibold ${
                          draft.device_visibility[dev]
                            ? "bg-watergreen/20 border-watergreen text-nightblue"
                            : "bg-white border-softblue text-textgray hover:bg-lightgray"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {dev === "desktop" ? "Escritorio" : dev === "tablet" ? "Tablet" : "Móvil"}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Programar inicio</Label>
                  <Input
                    data-testid={ATID.homepageEditorScheduleStart}
                    type="datetime-local"
                    value={draft.schedule_start}
                    onChange={(e) => setDraft({ ...draft, schedule_start: e.target.value })}
                    className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Finalizar</Label>
                  <Input
                    data-testid={ATID.homepageEditorScheduleEnd}
                    type="datetime-local"
                    value={draft.schedule_end}
                    onChange={(e) => setDraft({ ...draft, schedule_end: e.target.value })}
                    className="mt-1 h-9 bg-lightgray border-softblue text-sm"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Reemplazar por (al finalizar)</Label>
                <select
                  data-testid={ATID.homepageEditorReplacement}
                  value={draft.replacement_block_id}
                  onChange={(e) => setDraft({ ...draft, replacement_block_id: e.target.value })}
                  className="mt-1 h-9 w-full bg-lightgray border border-softblue px-2 text-sm text-nightblue"
                >
                  <option value="">— Sin reemplazo —</option>
                  {blocks.filter((b) => b.id !== draft.id).map((b) => (
                    <option key={b.id} value={b.id}>{b.title || KIND_META[b.kind]?.label}</option>
                  ))}
                </select>
              </div>

              {draft.kind === "live" && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">URL embebida de la señal</Label>
                  <Input
                    data-testid={ATID.homepageEditorEmbed}
                    value={draft.config?.embed_url || ""}
                    onChange={(e) => setDraft({ ...draft, config: { ...draft.config, embed_url: e.target.value } })}
                    placeholder="https://www.youtube.com/embed/…"
                    className="mt-1 h-9 bg-lightgray border-softblue text-sm font-mono text-xs"
                  />
                </div>
              )}

              {draft.kind === "main" && (
                <div className="pt-4 border-t border-softblue">
                  <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">
                    Composición editorial
                  </Label>
                  <p className="text-[11px] text-textgray mt-1 mb-3">
                    Elegí cómo se muestra la noticia principal junto a las secundarias en portada.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {LAYOUT_VARIANTS.map((v) => {
                      const selected = (draft.layout_variant || "A") === v.key;
                      return (
                        <button
                          key={v.key}
                          type="button"
                          data-testid={`homepage-layout-${v.key}`}
                          onClick={() => setDraft({ ...draft, layout_variant: v.key })}
                          className={`text-left p-3 border-2 transition-all ${
                            selected
                              ? "border-turquoise bg-lightgray shadow"
                              : "border-softblue bg-white hover:border-nightblue"
                          }`}
                        >
                          <div className="mb-2">
                            <LayoutThumb variant={v.key} />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] font-bold text-turquoise">{v.key}</span>
                            <span className="text-xs font-semibold text-blueblack">· {v.label}</span>
                          </div>
                          <div className="text-[10px] text-textgray mt-0.5">{v.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {draft.mode === "manual" && draft.kind !== "ad_slot" && draft.kind !== "live" && (
                <div className="pt-3 border-t border-softblue">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Noticias del bloque</Label>
                    <Button
                      data-testid={ATID.homepageEditorPickArticle}
                      variant="outline"
                      onClick={() => setPickerOpen(true)}
                      className="rounded-none h-8 border-softblue text-nightblue hover:bg-lightgray uppercase text-[10px] tracking-widest font-semibold"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Agregar
                    </Button>
                  </div>
                  {manualArticles.length === 0 ? (
                    <div className="text-xs text-textgray p-2 border border-dashed border-softblue">
                      Ninguna noticia seleccionada. En modo manual sin selección se completa con las últimas automáticamente.
                    </div>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndArticles}>
                      <SortableContext items={manualArticles.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-1.5">
                          {manualArticles.map((a) => (
                            <SortableArticleRow key={a.id} article={a} onRemove={removeManual} />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {draft && (
        <ArticlePicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          multi
          excludeIds={draft.manual_article_ids || []}
          onSelect={onPickArticles}
          title="Agregar noticias al bloque"
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar bloque?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{confirmDelete?.title || KIND_META[confirmDelete?.kind]?.label}</strong> de la portada. Las noticias en sí no se ven afectadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />
    </div>
  );
}


function PreviewDialog({ open, onOpenChange }) {
  const [device, setDevice] = useState("desktop");
  const src = `/?preview=1&t=${Date.now()}`;

  const widths = { desktop: 1280, tablet: 820, mobile: 390 };
  const width = widths[device];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={ATID.homepagePreviewDialog} className="max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif">Vista previa de la portada</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center gap-2 mb-3">
          {[
            { key: "desktop", label: "Escritorio", Icon: Monitor },
            { key: "tablet", label: "Tablet", Icon: Tablet },
            { key: "mobile", label: "Móvil", Icon: Smartphone },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={ATID.homepagePreviewDevice(key)}
              onClick={() => setDevice(key)}
              className={`inline-flex items-center gap-2 h-9 px-4 border uppercase text-xs tracking-widest font-semibold ${
                device === key ? "bg-nightblue border-nightblue text-white" : "border-softblue text-nightblue hover:bg-lightgray"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-auto bg-lightgray/50 border border-softblue flex items-start justify-center p-4">
          <iframe
            key={device}
            src={src}
            title="Vista previa"
            style={{ width, height: 700, maxWidth: "100%" }}
            className="bg-white border border-softblue"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
